/** Alert strip (Home page, wall tablets) and the grouped device list (expanded warnings): pure logic. */
import type { AlertItem, ModuleId } from '@home/shared';
import type { Alert } from './alerts.js';

const RANK: Record<Alert['tone'], number> = { alert: 0, caution: 1, info: 2 };

/** Warning / critical alerts only (info stays in the drawer), most serious first; `max` shown, the rest counted. */
export function stripAlerts(alerts: readonly Alert[], max: number): { shown: Alert[]; more: number; all: Alert[] } {
  const all = alerts.filter((a) => a.tone !== 'info').map((a, i) => [a, i] as const)
    .sort(([a, i], [b, j]) => RANK[a.tone] - RANK[b.tone] || i - j).map(([a]) => a);
  const n = Math.max(0, Math.floor(max));
  // "1 more" takes the same room as the alert it hides: show it instead.
  const shown = all.length <= n + 1 ? all : all.slice(0, n);
  return { shown, more: all.length - shown.length, all };
}

export type AlertTarget = { kind: 'link'; href: string } | { kind: 'history'; entityId: string } | null;

/**
 * Where tapping an alert goes: its dashboard when the user has it (hot tub, maintenance, vacuum…), else its entity's
 * history, else its room.
 */
export function alertTarget(
  a: Pick<Alert, 'module' | 'entityId' | 'roomId'>, home: string,
  can: { dashboard: (m: ModuleId) => boolean; roomHref: (roomId: string) => string },
): AlertTarget {
  if (a.module && can.dashboard(a.module)) return { kind: 'link', href: `/h/${home}/dashboards/${a.module}` };
  if (a.entityId) return { kind: 'history', entityId: a.entityId };
  if (a.roomId) return { kind: 'link', href: can.roomHref(a.roomId) };
  return null;
}

/** "45 min", "6 h", "3 d" (same steps as the server's devices_offline detail). */
export function ageShort(seconds: number): string {
  const m = Math.max(0, Math.floor(seconds / 60));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h} h` : `${Math.floor(h / 24)} d`;
}

/**
 * Items grouped by room (else integration, else "Other"), groups by size then name; longest offline first in each.
 */
export function groupItems(items: readonly AlertItem[]): Array<{ group: string; items: AlertItem[] }> {
  const by = new Map<string, AlertItem[]>();
  for (const it of items) {
    const g = it.room || it.integration || 'Other';
    let l = by.get(g);
    if (!l) by.set(g, (l = []));
    l.push(it);
  }
  return [...by.entries()]
    .map(([group, xs]) => ({ group, items: [...xs].sort((a, b) => a.since - b.since || a.name.localeCompare(b.name)) }))
    .sort((a, b) => b.items.length - a.items.length || a.group.localeCompare(b.group));
}
